/**
 * Prospector Prompts (Content Excellence System)
 *
 * Prompts for scoring content excellence and detecting raw gems.
 *
 * @module config/prompts/prospector-prompts
 */
// ═══════════════════════════════════════════════════════════════════════════
// PROSPECTOR PROMPTS
// ═══════════════════════════════════════════════════════════════════════════
export const PROSPECTOR_EXCELLENCE_SCORE = {
    id: 'PROSPECTOR_EXCELLENCE_SCORE',
    name: 'Prospector Excellence Scoring',
    description: 'Multi-dimensional excellence scoring for content',
    template: `You are an excellence assessor evaluating content quality across five dimensions.

{{#if context}}Context: {{context}}{{/if}}

Score each dimension from 0.0 to 1.0:

1. INSIGHT DENSITY (Novel ideas per paragraph)
   - Are there original thoughts or perspectives?
   - Is there intellectual substance worth preserving?

2. EXPRESSIVE POWER (Clarity and memorability)
   - Is the writing clear and easy to follow?
   - Are there memorable phrases or images?

3. EMOTIONAL RESONANCE (Reader connection)
   - Does the content evoke genuine feeling?
   - Would a reader feel moved or connected?

4. STRUCTURAL ELEGANCE (Flow and pacing)
   - Does the content flow naturally?
   - Is the pacing appropriate for the content?

5. VOICE AUTHENTICITY (Distinct, genuine)
   - Does this sound like a real person?
   - Is there a distinct perspective or voice?

Also extract up to 3 STANDOUT QUOTES - phrases worth preserving.

Content:
{{content}}

Respond with JSON:
{
  "insightDensity": 0.0-1.0,
  "expressivePower": 0.0-1.0,
  "emotionalResonance": 0.0-1.0,
  "structuralElegance": 0.0-1.0,
  "voiceAuthenticity": 0.0-1.0,
  "standoutQuotes": ["quote1", "quote2"],
  "reasoning": "Brief explanation of scoring"
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['prospector'],
};
export const PROSPECTOR_RAW_GEM_DETECTION = {
    id: 'PROSPECTOR_RAW_GEM_DETECTION',
    name: 'Prospector Raw Gem Detection',
    description: 'Detect buried insights in poorly written content',
    template: `You are analyzing content that may have valuable insights buried in poor writing.

A "raw gem" has:
- High INSIGHT quality (novel ideas, genuine observations)
- Low WRITING quality (unclear expression, poor flow)

Identify:
1. EXTRACTABLE INSIGHTS: Valuable ideas that deserve better expression
   - Location in text (beginning/middle/end or brief quote)
   - The insight itself (what's being said)
   - How it's currently written (original phrasing)
   - Your confidence this is a real insight (0-1)

2. NOISE TO REMOVE: Elements that obscure the insights
   - Redundant phrases
   - Unclear transitions
   - Off-topic tangents
   - Filler content

Content:
{{content}}

Respond with JSON:
{
  "isRawGem": true/false,
  "writingQuality": 0.0-1.0,
  "insightQuality": 0.0-1.0,
  "qualityGap": 0.0-1.0,
  "insights": [
    {
      "location": "string",
      "insight": "string",
      "originalPhrasing": "string",
      "confidence": 0.0-1.0
    }
  ],
  "noise": ["string"],
  "reasoning": "Brief explanation"
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['prospector'],
};
// ═══════════════════════════════════════════════════════════════════════════
// INLINE MIGRATION PROMPTS (formerly hardcoded in prospector)
// ═══════════════════════════════════════════════════════════════════════════
export const PROSPECTOR_INLINE_RAW_GEM = {
    id: 'PROSPECTOR_INLINE_RAW_GEM',
    name: 'Prospector Inline Raw Gem Analysis',
    description: 'Raw gem analysis (migrated from prospector.ts inline)',
    template: `You are analyzing content that has valuable insights buried in poor writing.

Identify:
1. EXTRACTABLE INSIGHTS: Valuable ideas that deserve better expression
   - Location in text (beginning/middle/end or quote)
   - The insight itself (what's being said)
   - How it's currently written
   - Your confidence this is a real insight (0-1)

2. NOISE TO REMOVE: Elements that obscure the insights
   - Redundant phrases
   - Unclear transitions
   - Off-topic tangents
   - Filler content

Respond with JSON:
{
  "insights": [
    {
      "location": "string",
      "insight": "string",
      "originalPhrasing": "string",
      "confidence": 0.0-1.0
    }
  ],
  "noise": ["string"]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['prospector'],
};
export const PROSPECTOR_PROMPTS = [
    PROSPECTOR_EXCELLENCE_SCORE,
    PROSPECTOR_RAW_GEM_DETECTION,
    PROSPECTOR_INLINE_RAW_GEM,
];
//# sourceMappingURL=prospector-prompts.js.map